import { Mark, mergeAttributes } from '@tiptap/core';
import type { CommentReply } from './comment-types';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      /**
       * Apply a comment annotation to the current selection.
       */
      setComment: (attributes: {
        commentId: string;
        replies: CommentReply[];
        resolved?: boolean;
      }) => ReturnType;
      /**
       * Update the thread (replies / resolved state) of an existing comment,
       * located anywhere in the document by its id.
       */
      updateComment: (
        commentId: string,
        attributes: { replies: CommentReply[]; resolved: boolean }
      ) => ReturnType;
      /**
       * Remove a comment annotation entirely, located by its id.
       */
      removeComment: (commentId: string) => ReturnType;
    };
  }
}

/**
 * Inline "comment" mark. Human-readable annotations that reviewers leave on a range of
 * text. The thread (replies + resolved flag) is stored in the mark's attributes so it
 * travels with the document JSON, but it is deliberately invisible to the agent — the
 * SDK prompt renderer never reads mark attributes and has no handler for this mark type,
 * so the underlying text renders unchanged and the notes are never sent to the LLM.
 */
export const CommentMark = Mark.create({
  name: 'comment',

  // Let comment marks coexist / overlap instead of evicting one another (a range can
  // carry more than one distinct comment).
  excludes: '',

  // Don't grow the comment when typing at either edge of the annotated range.
  inclusive: false,

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes) => {
          if (attributes['commentId'] == null) {
            return {};
          }
          return { 'data-comment-id': attributes['commentId'] };
        },
      },
      resolved: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-comment-resolved') === 'true',
        renderHTML: (attributes) => ({
          'data-comment-resolved':
            attributes['resolved'] === true ? 'true' : 'false',
        }),
      },
      // The thread lives here but is not written to the DOM — it is persisted via the
      // editor's JSON (getJSON), which is the source of truth for this editor.
      replies: {
        default: [] as CommentReply[],
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'cotera-comment' }),
      0,
    ];
  },

  addCommands() {
    return {
      setComment:
        (attributes) =>
        ({ commands }) =>
          commands.setMark(this.name, {
            commentId: attributes.commentId,
            replies: attributes.replies,
            resolved: attributes.resolved ?? false,
          }),

      updateComment:
        (commentId, attributes) =>
        ({ state, tr, dispatch }) => {
          const markType = state.schema.marks[this.name];
          if (markType === undefined) {
            return false;
          }

          let found = false;
          state.doc.descendants((node, pos) => {
            if (!node.isText) {
              return;
            }
            const existing = node.marks.find(
              (mark) =>
                mark.type === markType && mark.attrs['commentId'] === commentId
            );
            if (existing === undefined) {
              return;
            }
            found = true;
            if (dispatch) {
              const from = pos;
              const to = pos + node.nodeSize;
              // Remove only this specific comment (not other overlapping ones)
              // then re-apply it with the updated thread attributes.
              tr.removeMark(from, to, existing);
              tr.addMark(
                from,
                to,
                markType.create({
                  commentId,
                  replies: attributes.replies,
                  resolved: attributes.resolved,
                })
              );
            }
          });

          return found;
        },

      removeComment:
        (commentId) =>
        ({ state, tr, dispatch }) => {
          const markType = state.schema.marks[this.name];
          if (markType === undefined) {
            return false;
          }

          let found = false;
          state.doc.descendants((node, pos) => {
            if (!node.isText) {
              return;
            }
            const existing = node.marks.find(
              (mark) =>
                mark.type === markType && mark.attrs['commentId'] === commentId
            );
            if (existing === undefined) {
              return;
            }
            found = true;
            if (dispatch) {
              tr.removeMark(pos, pos + node.nodeSize, existing);
            }
          });

          return found;
        },
    };
  },
});
